import type {PerksAnalyticsOverviewParamsADto} from '@cohort/admin-schemas/perkAnalytics';
import {usePerkIntegrations} from '@cohort/merchants/apps/usePerkIntegrations';
import AdminPage from '@cohort/merchants/components/AdminPage';
import Button from '@cohort/merchants/components/buttons/Button';
import CardListDisplayToggler from '@cohort/merchants/components/buttons/CardListDisplayToggler';
import Filters from '@cohort/merchants/components/filters/Filters';
import SingleChoiceFilter from '@cohort/merchants/components/filters/SingleChoiceFilter';
import {usePerkAnalytics} from '@cohort/merchants/hooks/api/Perks';
import {
  analyticsSortFilter,
  integrationFilter,
  statusFilter,
  typeFilter,
  usePerksListFilters,
} from '@cohort/merchants/hooks/filters/perksListFilters';
import {usePerksDisplay} from '@cohort/merchants/hooks/stores/perksDisplay';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {usePerkUtils} from '@cohort/merchants/hooks/usePerkUtils';
import PageLayout from '@cohort/merchants/layouts/PageLayout';
import {getPerkCreateRoute} from '@cohort/merchants/lib/Pages';
import {
  trackPerkCreateButtonClicked,
  trackPerksDisplayChanged,
  trackPerksListPageViewed,
  trackPerksListTableAppsFilterChanged,
  trackPerksListTableStatusFilterChanged,
  trackPerksListTableTypesFilterChanged,
} from '@cohort/merchants/lib/Tracking';
import PerksList from '@cohort/merchants/pages/perks/overview/PerksList';
import {Desktop, Plus, Tag} from '@phosphor-icons/react';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {shallow} from 'zustand/shallow';

const PAGE_SIZE = 20;

const PerksOverviewPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const {t} = useTranslation('pages', {keyPrefix: 'perks.overview.page'});
  const navigate = useNavigate();
  const {display, setDisplay} = usePerksDisplay(
    store => ({
      display: store.display,
      setDisplay: store.setDisplay,
    }),
    shallow
  );
  const filters = usePerksListFilters(/^(\/test)?\/perks$/u);
  const {getPerkTypeData} = usePerkUtils();
  const {getPerkIntegration, getPerkIntegrationTitle} = usePerkIntegrations();

  // i18nOwl-ignore [status.archived, status.draft, status.live]
  const statusFilterOptions = statusFilter.map(status => ({
    value: status,
    label: t(`status.${status}`),
    active: filters.status ? filters.status.includes(status) : false,
  }));

  const integrationFilterOptions = integrationFilter.map(integrationId => {
    return {
      value: integrationId,
      label: getPerkIntegrationTitle(getPerkIntegration(integrationId)),
      active: filters.integrations ? filters.integrations.includes(integrationId) : false,
    };
  });

  const typeFilterOptions = typeFilter.map(type => {
    return {
      value: type,
      label: getPerkTypeData(type).title,
      active: filters.types ? filters.types.includes(type) : false,
    };
  });

  // i18nOwl-ignore [sortBy.-createdAt, sortBy.-updatedAt, sortBy.app, sortBy.name]
  const sortingOptions = analyticsSortFilter.map(sort => ({
    value: sort,
    label: t(`sortBy.${sort}`),
    active: filters.sort === sort,
  }));

  const {data, isLoading, isError} = usePerkAnalytics(merchantId, {
    page: filters.page,
    pageSize: PAGE_SIZE,
    orderBy: filters.analyticsSort,
    statuses: filters.status,
    integrations: filters.integrations,
    types: filters.types,
  });
  const [pagination, perks] = data ?? [];

  function handleCreatePerkClick(): void {
    trackPerkCreateButtonClicked();
    navigate(getPerkCreateRoute().path);
  }

  useEffect(() => {
    trackPerksListPageViewed();
  }, []);

  const headerConfig = {
    title: t('perks'),
    subtitle: t('subtitle'),
    topRightElements: (
      <Button key="createPerkBtn" onClick={handleCreatePerkClick}>
        <Plus className="-ml-1 mr-2 h-5 w-5" />
        {t('createPerk')}
      </Button>
    ),
  };

  const paginationConfig = {
    pagination,
    onPageChange: filters.setPage,
  };

  return (
    <PageLayout title={t('perks')}>
      <AdminPage header={headerConfig} pagination={paginationConfig}>
        <div className="mb-7 flex w-full flex-col justify-between sm:flex-row sm:items-center">
          <Filters
            filters={[
              {
                label: t('filterByStatus'),
                options: statusFilterOptions,
                type: 'multiple',
                onChange: statusOptions => {
                  const activeFilterOptions = statusOptions.filter(o => o.active);

                  trackPerksListTableStatusFilterChanged(activeFilterOptions.map(o => o.value));
                  filters.setStatus(
                    activeFilterOptions.map(
                      o => o.value
                    ) as PerksAnalyticsOverviewParamsADto['statuses']
                  );
                },
              },
              {
                label: t('filterByApp'),
                options: integrationFilterOptions,
                type: 'multiple',
                icon: <Desktop className="mr-1.5 inline-block h-4 w-4" />,
                onChange: typeOptions => {
                  const activeFilterOptions = typeOptions.filter(o => o.active);

                  trackPerksListTableAppsFilterChanged(activeFilterOptions.map(o => o.value));
                  filters.setIntegrations(
                    activeFilterOptions.map(
                      o => o.value
                    ) as PerksAnalyticsOverviewParamsADto['integrations']
                  );
                },
              },
              {
                label: t('filterByType'),
                options: typeFilterOptions,
                type: 'multiple',
                icon: <Tag className="mr-1.5 inline-block h-4 w-4" />,
                onChange: typeOptions => {
                  const activeFilterOptions = typeOptions.filter(o => o.active);
                  trackPerksListTableTypesFilterChanged(activeFilterOptions.map(o => o.value));
                  filters.setTypes(
                    activeFilterOptions.map(
                      o => o.value
                    ) as PerksAnalyticsOverviewParamsADto['types']
                  );
                },
              },
            ]}
          />
          <div className="flex flex-row space-x-4">
            <SingleChoiceFilter
              options={sortingOptions}
              onChange={option =>
                filters.setAnalyticsSort(
                  option.value as PerksAnalyticsOverviewParamsADto['orderBy']
                )
              }
              prefix={t('sortBy.title')}
            />
            <CardListDisplayToggler
              display={display}
              setDisplay={display => {
                trackPerksDisplayChanged(display);
                setDisplay(display);
              }}
            />
          </div>
        </div>
        <PerksList
          perks={perks}
          isError={isError}
          isLoading={isLoading}
          onHandleCreatePerkClick={handleCreatePerkClick}
        />
      </AdminPage>
    </PageLayout>
  );
};

export default PerksOverviewPage;
